import React, { useState, useEffect } from 'react';
import { FileText, Plus, Edit3, Trash2, Tag, Folder, Calendar, User, Search, Filter } from 'lucide-react';
import api from '../utils/api';
import NotesModal from './NotesModal';


const NotesTab = ({ client, projects, user }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadNotes();
    loadCategories();
  }, [client.id]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (selectedProject) {
        params.append('project_id', selectedProject);
      }
      
      if (selectedCategory) {
        params.append('category', selectedCategory);
      }

      const response = await api.get(
        `/clients/${client.id}/notes?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      setNotes(response.data);
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get(`/notes/categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setCategories(response.data.map(cat => cat.category));
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleCreateNote = () => {
    setEditingNote(null);
    setShowNotesModal(true);
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setShowNotesModal(true);
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    try {
      await api.delete(`/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      setNotes(notes.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  const handleNoteSave = (savedNote) => {
    if (editingNote) {
      // Update existing note
      setNotes(notes.map(note => 
        note.id === savedNote.id ? savedNote : note
      ));
    } else {
      // Add new note
      setNotes([savedNote, ...notes]);
    }
    loadCategories(); // Refresh categories in case a new one was added
  };

  const getProjectName = (projectId) => {
    if (!projectId) return null;
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown Project';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter notes based on search query
  const filteredNotes = notes.filter(note => {
    const matchesSearch = searchQuery === '' || 
      note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (note.category && note.category.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading notes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Notes</h2>
          <p className="text-gray-600">Manage client notes and documentation</p>
        </div>
        <button
          onClick={handleCreateNote}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
        >
          <Plus className="w-4 h-4" />
          <span>New Note</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Project Filter */}
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">All Projects</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          {/* Apply Filters Button */}
          <button
            onClick={loadNotes}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            <Filter className="w-4 h-4" />
            <span>Apply Filters</span>
          </button>
        </div>
      </div>

      {/* Notes List */}
      {filteredNotes.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery || selectedProject || selectedCategory ? 'No matching notes found' : 'No notes yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || selectedProject || selectedCategory 
              ? 'Try adjusting your search criteria or filters.'
              : `Start documenting important information about ${client.name}.`
            }
          </p>
          {!searchQuery && !selectedProject && !selectedCategory && (
            <button
              onClick={handleCreateNote}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Create First Note
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredNotes.map((note) => (
            <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-purple-300 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{note.title}</h3>
                  
                  {/* Metadata */}
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(note.created_at)}</span>
                    </div>
                    
                    {note.category && (
                      <div className="flex items-center space-x-1">
                        <Tag className="w-4 h-4" />
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {note.category}
                        </span>
                      </div>
                    )}
                    
                    {note.project_id && (
                      <div className="flex items-center space-x-1">
                        <Folder className="w-4 h-4" />
                        <span className="text-purple-600">{getProjectName(note.project_id)}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Content Preview */}
                  <div 
                    className="text-gray-700 line-clamp-3 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ 
                      __html: note.content.length > 200 
                        ? note.content.substring(0, 200) + '...' 
                        : note.content 
                    }}
                    style={{
                      /* Custom styles for rich text content */
                    }}
                  />
                  
                  <style jsx>{`
                    .prose ul {
                      list-style-type: disc;
                      margin-left: 1.25rem;
                      margin-top: 0.25rem;
                      margin-bottom: 0.25rem;
                    }
                    
                    .prose ol {
                      list-style-type: decimal;
                      margin-left: 1.25rem;
                      margin-top: 0.25rem;
                      margin-bottom: 0.25rem;
                    }
                    
                    .prose li {
                      margin-bottom: 0.125rem;
                    }
                    
                    .prose b,
                    .prose strong {
                      font-weight: 600;
                    }
                    
                    .prose u {
                      text-decoration: underline;
                    }
                    
                    .prose p {
                      margin-bottom: 0.25rem;
                    }
                    
                    .prose p:last-child {
                      margin-bottom: 0;
                    }
                  `}</style>
                </div>
                
                {/* Actions */}
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleEditNote(note)}
                    className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Edit note"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteNote(note.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete note"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Create Task Button */}
              <div className="pt-3 border-t border-gray-100">
                <button
                  disabled
                  className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-500 text-sm rounded-lg cursor-not-allowed opacity-50"
                  title="Create task from note (coming soon)"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Task from Note</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Notes Modal */}
      <NotesModal
        isOpen={showNotesModal}
        onClose={() => {
          setShowNotesModal(false);
          setEditingNote(null);
        }}
        client={client}
        projects={projects}
        existingNote={editingNote}
        onSave={handleNoteSave}
      />
    </div>
  );
};

export default NotesTab;